import { useLanguage } from '@/i18n/LanguageContext';

export type BannerTone = 'ok' | 'info' | 'warn' | 'error';

export type BannerAction = { readonly id: string; readonly label: string };

/** What the operator is told after a scan: big, coloured, no internal codes. */
export type ScanBanner = {
  readonly tone: BannerTone;
  readonly title: string;
  readonly detail?: string;
  readonly actions?: readonly BannerAction[];
};

/**
 * The result of the last scan. The region is always mounted and `aria-live="polite"`, so a screen reader
 * announces each new result; `data-tone` lets the tests (and styles) tell the kinds apart.
 */
export function ScanBannerView({ banner, onAction }: { readonly banner: ScanBanner | null; readonly onAction?: (id: string) => void }): JSX.Element {
  const { t } = useLanguage();
  return (
    <div className="scan-banner-region" role="status" aria-live="polite" aria-atomic="true" data-testid="scan-banner-region">
      {banner && (
        <div className={`scan-banner scan-banner--${banner.tone}`} data-testid="scan-banner" data-tone={banner.tone} aria-label={t('scan.result')}>
          <div className="scan-banner-title">{banner.title}</div>
          {banner.detail && <div className="scan-banner-detail">{banner.detail}</div>}
          {banner.actions && banner.actions.length > 0 && (
            <div className="scan-banner-actions">
              {banner.actions.map((a) => (
                <button key={a.id} type="button" className="scan-banner-action" onClick={() => onAction?.(a.id)}>{a.label}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
